import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Search, Lock, Unlock, CheckCheck, Check, ExternalLink, Video as VideoIcon, Mic, FileText, Loader2 } from 'lucide-react';
import PageHeader from '../../components/PageHeader';
import { toast } from 'sonner';
import api, { apiError, fmtDateTime, fmtResponse, mediaUrl, parseUTC } from '../../lib/api';
import { chargerComptes } from '../../lib/comptesPartages';
import { LightboxDialog } from './vault/Dialogs';

const respColor = (s) => {
  if (s === null || s === undefined) return 'text-slate-600';
  if (s < 600) return 'text-emerald-400';
  if (s < 3600) return 'text-amber-400';
  return 'text-rose-400';
};

const isoLocal = (d) => new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 10);
const todayISO = () => isoLocal(new Date());
// La veille (locale) : le backend filtre les dates en UTC ; juste après minuit local,
// les messages « de maintenant » sont encore datés d'hier en UTC → on part d'hier pour
// que la vue ne soit jamais vide au changement de journée.
const yesterdayISO = () => { const d = new Date(); d.setDate(d.getDate() - 1); return isoLocal(d); };

// #147 (16/09, le journal figeait le PC) : 100 lignes à la fois, « Voir plus » pour la suite, AUCUNE vidéo chargée
// dans la liste (la vidéo ne se charge qu'à l'ouverture de l'aperçu), et le rafraîchissement ne demande que les
// nouveaux messages. Les filtres sont faits par le serveur (sinon ils ne porteraient que sur les lignes chargées).
const PAGE = 100;
const MAX_NOUVEAUX = 500;
const RECOUVREMENT_MS = 5 * 60000; // un envoi importé de Telegram peut arriver en base quelques secondes en retard

// Les lignes portent `cursor` (« date UTC à la microseconde|id ») : trier ce texte = trier par date.
const fusionner = (anciennes, nouvelles) => {
  const parId = new Map(anciennes.map(r => [r.id, r]));
  nouvelles.forEach(r => parId.set(r.id, r));
  return [...parId.values()].sort((a, b) => ((a.cursor || '') < (b.cursor || '') ? 1 : (a.cursor || '') > (b.cursor || '') ? -1 : 0));
};

export default function MessageLogs() {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [chargement, setChargement] = useState(true);
  const [plusEnCours, setPlusEnCours] = useState(false);
  const [finAtteinte, setFinAtteinte] = useState(false);
  const [accounts, setAccounts] = useState([]);
  const [allEmployees, setAllEmployees] = useState([]); // liste COMPLÈTE de l'équipe (pas seulement ceux des messages chargés)
  const [account, setAccount] = useState('all');
  const [employee, setEmployee] = useState('all');
  const [media, setMedia] = useState('all');
  const [qConv, setQConv] = useState('');
  const [qMsg, setQMsg] = useState('');
  const [qConvServeur, setQConvServeur] = useState('');
  const [qMsgServeur, setQMsgServeur] = useState('');
  // Par défaut : AUJOURD'HUI (journée complète) → à jour + tous les employés actifs du jour.
  // Bouton « Live ✕ » pour repasser au flux temps réel, « 7 jours » pour élargir.
  const [fromDate, setFromDate] = useState(yesterdayISO());
  const [toDate, setToDate] = useState(todayISO());
  const [preview, setPreview] = useState(null); // media_obj opened in the lightbox
  const demande = useRef(0);         // numéro de la dernière demande : une réponse périmée (filtre changé entre-temps) est ignorée
  const lignes = useRef([]);
  lignes.current = rows;

  // Recherche tapée : envoyée au serveur 350 ms après la dernière frappe.
  useEffect(() => { const t = setTimeout(() => setQConvServeur(qConv.trim()), 350); return () => clearTimeout(t); }, [qConv]);
  useEffect(() => { const t = setTimeout(() => setQMsgServeur(qMsg.trim()), 350); return () => clearTimeout(t); }, [qMsg]);

  const params = useCallback(() => {
    // tz_offset = new Date().getTimezoneOffset() (minutes, UTC-local) → le backend borne la
    // journée dans TON heure locale (plus de journée vide près de minuit).
    const p = { tz_offset: new Date().getTimezoneOffset() };
    if (fromDate) p.from_date = fromDate;
    if (toDate) p.to_date = toDate;
    if (account !== 'all') p.account_id = account;
    if (employee !== 'all') p.employee = employee;
    if (media !== 'all') p.media = media;
    if (qConvServeur) p.q_conv = qConvServeur;
    if (qMsgServeur) p.q_msg = qMsgServeur;
    return p;
  }, [fromDate, toDate, account, employee, media, qConvServeur, qMsgServeur]);

  const load = useCallback(async () => {
    const n = ++demande.current;
    setChargement(true);
    try {
      const { data } = await api.get('/reports/message-logs', { params: { ...params(), limit: PAGE } });
      if (n !== demande.current) return;
      const liste = Array.isArray(data) ? data : [];
      setRows(liste);
      setFinAtteinte(liste.length < PAGE);
    } catch (e) {
      if (n === demande.current) toast.error(apiError(e, 'Impossible de charger les logs'));
    } finally {
      if (n === demande.current) setChargement(false);
    }
  }, [params]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    chargerComptes().then(setAccounts).catch(() => {});
    // Liste COMPLÈTE des employés (pour le filtre) — indépendante des messages chargés,
    // sinon un employé sans envoi récent n'apparaissait jamais dans le menu.
    api.get('/chatters').then(({ data }) => setAllEmployees((data || []).map(c => c.name).filter(Boolean))).catch(() => {});
  }, []);

  const voirPlus = async () => {
    const dernier = rows[rows.length - 1];
    if (!dernier?.cursor || plusEnCours) return;
    const n = demande.current;
    setPlusEnCours(true);
    try {
      const { data } = await api.get('/reports/message-logs', { params: { ...params(), limit: PAGE, before: dernier.cursor } });
      if (n !== demande.current) return;
      const liste = Array.isArray(data) ? data : [];
      setRows(prev => fusionner(prev, liste));
      setFinAtteinte(liste.length < PAGE);
    } catch (e) {
      toast.error(apiError(e, 'Impossible de charger la suite'));
    } finally {
      setPlusEnCours(false);
    }
  };

  // Rafraîchissement toutes les 30 s SEULEMENT si la période va jusqu'à aujourd'hui et que l'onglet est visible :
  // on ne demande que les messages plus récents que ceux affichés (avec 5 min de recouvrement), ajoutés en haut.
  const inclutAujourdhui = !toDate || toDate >= todayISO();
  useEffect(() => {
    if (!inclutAujourdhui) return undefined;
    let enCours = false;
    const rafraichir = async () => {
      if (document.visibilityState !== 'visible' || enCours) return;
      const affichees = lignes.current;
      const n = demande.current;
      if (!affichees.length) { load(); return; }
      const plusRecente = parseUTC(affichees[0].created_at).getTime();
      if (Number.isNaN(plusRecente)) return;
      enCours = true;
      try {
        const depuis = new Date(plusRecente - RECOUVREMENT_MS).toISOString();
        const { data } = await api.get('/reports/message-logs', { params: { ...params(), limit: MAX_NOUVEAUX, after: depuis } });
        if (n !== demande.current || !Array.isArray(data)) return;
        if (data.length >= MAX_NOUVEAUX) { load(); return; } // énorme arrivée : on repart de la 1re page
        if (data.length) setRows(prev => fusionner(prev, data));
      } catch { /* silencieux : on réessaie au prochain passage */ } finally {
        enCours = false;
      }
    };
    const t = setInterval(rafraichir, 30000);
    const auRetour = () => { if (document.visibilityState === 'visible') rafraichir(); };
    document.addEventListener('visibilitychange', auRetour);
    return () => { clearInterval(t); document.removeEventListener('visibilitychange', auRetour); };
  }, [inclutAujourdhui, params, load]);

  const last7Days = () => {
    const now = new Date();
    const start = new Date(); start.setDate(now.getDate() - 6);
    setFromDate(isoLocal(start)); setToDate(isoLocal(now));
  };

  const employees = Array.from(new Set([...allEmployees, ...rows.map(r => r.employee)])).filter(e => e && e !== '—').sort((a, b) => a.localeCompare(b));
  const filtreActif = account !== 'all' || employee !== 'all' || media !== 'all' || qConvServeur || qMsgServeur;

  return (
    <div className="p-6 md:p-8 max-w-7xl mx-auto fade-in" data-testid="message-logs-page">
      <PageHeader title="Logs de messages" subtitle={`${rows.length} messages affichés · traçabilité complète & temps de réponse`} onRefresh={load} />

      <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-4 mb-4 space-y-3">
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative flex-1 min-w-[180px]">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
            <Input data-testid="logs-search-conv" value={qConv} onChange={e => setQConv(e.target.value)} placeholder="Rechercher une conversation..." className="bg-white/5 border-white/10 text-white pl-9 h-9" />
          </div>
          <div className="relative flex-1 min-w-[180px]">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
            <Input data-testid="logs-search-msg" value={qMsg} onChange={e => setQMsg(e.target.value)} placeholder="Rechercher un message..." className="bg-white/5 border-white/10 text-white pl-9 h-9" />
          </div>
          <select data-testid="logs-account-filter" value={account} onChange={e => setAccount(e.target.value)} className="bg-white/5 border border-white/10 rounded-lg px-3 text-xs text-white h-9 outline-none">
            <option value="all" className="bg-[#0B0B0E]">Tous les comptes</option>
            {accounts.map(a => <option key={a.id} value={a.id} className="bg-[#0B0B0E]">{a.name}</option>)}
          </select>
          <select data-testid="logs-employee-filter" value={employee} onChange={e => setEmployee(e.target.value)} className="bg-white/5 border border-white/10 rounded-lg px-3 text-xs text-white h-9 outline-none">
            <option value="all" className="bg-[#0B0B0E]">Tous les employés</option>
            {employees.map(e => <option key={e} value={e} className="bg-[#0B0B0E]">{e}</option>)}
          </select>
          <select data-testid="logs-media-filter" value={media} onChange={e => setMedia(e.target.value)} className="bg-white/5 border border-white/10 rounded-lg px-3 text-xs text-white h-9 outline-none">
            <option value="all" className="bg-[#0B0B0E]">Tous les médias</option>
            <option value="paid" className="bg-[#0B0B0E]">Payant</option>
            <option value="free" className="bg-[#0B0B0E]">Gratuit</option>
            <option value="none" className="bg-[#0B0B0E]">Sans média</option>
          </select>
          <input type="date" data-testid="logs-from-date" value={fromDate} onChange={e => setFromDate(e.target.value)} title="Du" className="bg-white/5 border border-white/10 rounded-lg px-2 text-xs text-white h-9 outline-none" />
          <span className="text-xs text-slate-500">→</span>
          <input type="date" data-testid="logs-to-date" value={toDate} onChange={e => setToDate(e.target.value)} title="Au" className="bg-white/5 border border-white/10 rounded-lg px-2 text-xs text-white h-9 outline-none" />
          <button type="button" data-testid="logs-7-jours" onClick={last7Days} className="px-2.5 h-9 rounded-lg text-xs text-slate-200 bg-white/5 border border-white/10 hover:bg-white/10">7 jours</button>
          {(fromDate || toDate) && (
            <button type="button" data-testid="logs-clear-dates" onClick={() => { setFromDate(''); setToDate(''); }} className="px-2.5 h-9 rounded-lg text-xs text-indigo-300 bg-indigo-500/10 border border-indigo-400/30 hover:bg-indigo-500/20">Live ✕</button>
          )}
        </div>
        <div className="flex items-center gap-5">
          {chargement && <span className="inline-flex items-center gap-1.5 text-xs text-slate-400"><Loader2 size={12} className="animate-spin" /> Chargement…</span>}
          <span className="ml-auto text-xs text-slate-500" data-testid="logs-compteur">
            {rows.length} messages{finAtteinte ? '' : ' (les plus récents)'}{filtreActif ? ' · filtrés' : ''}
          </span>
        </div>
      </div>

      <div className="rounded-2xl border border-white/10 bg-white/[0.02] overflow-hidden">
        <table className="w-full table-fixed">
          {/* Fixed column widths so all 8 columns always fit the container — no
              clipped Time/Response columns, no horizontal scroll. Long text wraps. */}
          <colgroup>
            <col className="w-[9%]" />
            <col className="w-[7%]" />
            <col className="w-[14%]" />
            <col className="w-[33%]" />
            <col className="w-[9%]" />
            <col className="w-[8%]" />
            <col className="w-[12%]" />
            <col className="w-[8%]" />
          </colgroup>
          <thead>
            <tr className="text-left text-xs uppercase tracking-wider text-slate-500 border-b border-white/5">
              <th className="px-4 py-3 font-semibold">Employé</th>
              <th className="px-4 py-3 font-semibold">Compte</th>
              <th className="px-4 py-3 font-semibold">Conversation</th>
              <th className="px-4 py-3 font-semibold">Message</th>
              <th className="px-4 py-3 font-semibold">Média</th>
              <th className="px-4 py-3 font-semibold">Statut</th>
              <th className="px-4 py-3 font-semibold">Heure</th>
              <th className="px-4 py-3 font-semibold text-right">Réponse</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-white/5" data-testid="logs-lignes">
            {rows.map(r => (
              <tr key={r.id} className="hover:bg-white/[0.02] [&>td]:align-top" data-testid={`log-row-${r.id}`}>
                <td className="px-4 py-3 text-sm text-white whitespace-nowrap">{r.employee}</td>
                <td className="px-4 py-3 text-sm text-slate-300 whitespace-nowrap">{r.account_name}</td>
                <td className="px-4 py-3 text-sm text-slate-300 align-top">
                  <button
                    data-testid={`jump-to-conv-${r.id}`}
                    onClick={() => navigate(`/dashboard/messages?account=${r.account_id}&conv=${r.conversation_id}`)}
                    className="group/jump inline-flex items-start gap-1.5 text-left text-indigo-300 hover:text-indigo-200 hover:underline underline-offset-2"
                    title="Ouvrir la conversation dans l'inbox"
                  >
                    <span className="break-words">{r.conversation}</span>
                    <ExternalLink size={11} className="shrink-0 mt-1 opacity-0 group-hover/jump:opacity-100 transition-opacity" />
                  </button>
                </td>
                <td className="px-4 py-3 text-sm text-slate-400 align-top">
                  <span className="block whitespace-pre-wrap break-words">{r.message}</span>
                  {r.has_link && <span className="text-[10px] text-amber-400/80">contient un lien DropFans</span>}
                </td>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-2">
                    {(r.media_obj?.url || r.media_obj?.id) && (
                      <button
                        type="button"
                        data-testid={`log-media-open-${r.id}`}
                        onClick={() => setPreview({ ...r.media_obj, tags: [] })}
                        className="cursor-zoom-in shrink-0 hover:opacity-80 transition-opacity"
                        title="Clique pour voir"
                      >
                        {/* Vignette photo chargée seulement quand la ligne arrive à l'écran ; jamais de <video> ici. */}
                        {r.media_obj.type === 'photo' && r.media_obj.url && (
                          <img src={mediaUrl(r.media_obj.url)} alt={r.media_obj.name || ''} loading="lazy" decoding="async" width={40} height={40} className="w-10 h-10 rounded-md object-cover border border-white/10" data-testid={`log-thumb-${r.id}`} />
                        )}
                        {r.media_obj.type === 'photo' && !r.media_obj.url && (
                          <div className="w-10 h-10 rounded-md bg-white/5 border border-white/10" data-testid={`log-thumb-${r.id}`} />
                        )}
                        {r.media_obj.type === 'video' && (
                          <div className="w-10 h-10 rounded-md bg-black/40 border border-white/10 flex items-center justify-center" data-testid={`log-thumb-${r.id}`}>
                            <VideoIcon size={14} className="text-white/80" />
                          </div>
                        )}
                        {r.media_obj.type === 'voice' && (
                          <div className="w-10 h-10 rounded-md bg-indigo-500/15 border border-indigo-500/30 flex items-center justify-center" data-testid={`log-thumb-${r.id}`}><Mic size={12} className="text-indigo-300" /></div>
                        )}
                        {r.media_obj.type === 'document' && (
                          <div className="w-10 h-10 rounded-md bg-white/5 border border-white/10 flex items-center justify-center" data-testid={`log-thumb-${r.id}`}><FileText size={12} className="text-slate-400" /></div>
                        )}
                      </button>
                    )}
                    {r.media_obj && <span className="text-[11px] text-slate-500 capitalize">{r.media_obj.type}</span>}
                    {!r.media_obj && <span className="text-xs text-slate-600">—</span>}
                  </div>
                </td>
                <td className="px-4 py-3">
                  <div className="flex flex-col gap-1 items-start">
                    {r.has_link
                      ? (r.paid_converted
                        ? <Badge className="bg-emerald-500/15 text-emerald-300 border-emerald-500/30" title="Le fan a payé ce PPV"><Check size={9} className="mr-1" /> payé</Badge>
                        : <Badge className="bg-rose-500/15 text-rose-300 border-rose-500/30" title="Lien de paiement envoyé — pas encore payé"><Lock size={9} className="mr-1" /> pas payé</Badge>)
                      : r.media === 'free'
                        ? <Badge className="bg-sky-500/15 text-sky-300 border-sky-500/30" title="Contenu envoyé sans lien de paiement"><Unlock size={9} className="mr-1" /> gratuit</Badge>
                        : <span className="text-xs text-slate-600">—</span>}
                    <span className="text-[10px] text-slate-500 flex items-center gap-1">
                      {r.status === 'seen'
                        ? <><CheckCheck size={10} className="text-sky-400" /> vu</>
                        : <><Check size={10} /> envoyé</>}
                    </span>
                  </div>
                </td>
                <td className="px-4 py-3 text-xs text-slate-500 whitespace-nowrap">{fmtDateTime(r.created_at)}</td>
                <td className={`px-4 py-3 text-right text-sm font-semibold whitespace-nowrap ${respColor(r.response_seconds)}`} data-testid={`response-${r.id}`}>
                  {fmtResponse(r.response_seconds)}
                </td>
              </tr>
            ))}
            {!chargement && rows.length === 0 && (
              <tr><td colSpan={8} className="px-4 py-10 text-center text-sm text-slate-500">Aucun message sur cette période{filtreActif ? ' avec ces filtres' : ''}.</td></tr>
            )}
          </tbody>
        </table>
        {!finAtteinte && rows.length > 0 && (
          <div className="border-t border-white/5 p-3 flex justify-center">
            <button type="button" data-testid="logs-voir-plus" onClick={voirPlus} disabled={plusEnCours}
              className="inline-flex items-center gap-2 px-4 h-9 rounded-lg text-xs text-slate-200 bg-white/5 border border-white/10 hover:bg-white/10 disabled:opacity-50">
              {plusEnCours && <Loader2 size={12} className="animate-spin" />} Voir plus ({PAGE} messages plus anciens)
            </button>
          </div>
        )}
      </div>

      <LightboxDialog preview={preview} onClose={() => setPreview(null)} tagColors={{}} />
    </div>
  );
}
